import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { targetKey, type BindingTarget } from "../../lib/analyticsFeeds";

/**
 * The confirmation raised when switching off the sample row would clear the
 * workflow inputs it fills.
 *
 * Switching it off cannot simply hide those answers: the row's picker is the
 * only surface that shows them, so a hidden binding would keep feeding every
 * run with nothing on the table to say so. Each input is named, because the
 * cost is those inputs going unanswered.
 */
interface ClearSampleRowInputsDialogProps {
  /**
   * The pairs this dialog names, read when it opens. It names the cost; the
   * caller decides what the confirm clears, against the bindings as they stand
   * at that moment, so a pair listed here may belong to another row by then
   * and go untouched. Naming a superset is the safe direction: the auditor is
   * warned about every input that could lose its answer.
   */
  targets: readonly BindingTarget[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function ClearSampleRowInputsDialog({
  targets,
  open,
  onOpenChange,
  onConfirm,
}: ClearSampleRowInputsDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Stop filling inputs from each sample's own row?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="flex flex-col gap-2">
              <span className="block">
                Each sample's own row fills these workflow inputs. Turning it
                off clears them:
              </span>
              <ul className="list-disc pl-5">
                {/* The pair, as the picker names it: two columns can declare
                    the same parameter label. */}
                {targets.map((target) => (
                  <li key={targetKey(target)}>
                    {`${target.columnName} · ${target.label}`}
                  </li>
                ))}
              </ul>
              <span className="block">
                They stay unanswered until another row fills them.
              </span>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={onConfirm}>
            Clear inputs
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
